import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import SectionHeader from './ui/SectionHeader';

const Projects = () => {
  const [expandedId, setExpandedId] = useState(null);

  const projects = [
    {
      id: 1,
      code: 'PDSI',
      title: 'PDSI Rig #09.2/N80UE-E, onshore rig',
      type: 'Drilling',
      period: 'Mar 2026 - Sep 2026',
      company: 'PT Pertamina Drilling Services Indonesia',
      status: 'DONE',
      description: 'Onshore drilling on the 1,500 HP PDSI #09.2/N80UE rig.',
    },
    {
      id: 2,
      code: 'PTB6',
      title: 'Patimban Port, package 6',
      type: 'Port',
      period: 'Feb 2024 - Nov 2025',
      company: 'PTRPWJ (Penta-Toyo-Rinkai-PP-Wika-Jaya Konstruksi) Consortium',
      status: 'DONE',
      description: 'Patimban Port development, package 6.',
    },
    {
      id: 3,
      code: 'SUTET',
      title: 'SUTET 500kV tower assessment, Tanjung Jati to Cibatu',
      type: 'Assessment',
      period: 'Dec 2023 - Feb 2024',
      company: 'PT PLN Pusat Manajemen Proyek (PUSMANPRO), as contractor',
      status: 'DONE',
      description: 'Assessment of the SUTET 500kV towers from Tanjung Jati to Cibatu.',
    },
    {
      id: 4,
      code: 'BTNG2',
      title: 'PLTU Batang 2x1000MW, CW pipe backfilling',
      type: 'Power',
      period: 'Jan 2021 - Oct 2023',
      company: 'Wakachiku, main contractor',
      client: 'Mitsui Engineering and Shipbuilding (MES)',
      status: 'DONE',
      description: 'Backfilling on the intake and outfall CW pipe.',
    },
    {
      id: 5,
      code: 'PTB2',
      title: 'Patimban Port, package 2 breakwater',
      type: 'Marine',
      period: 'Jan 2020 - Jan 2021',
      company: 'TAW-JV (Toyo-Adhi-Wakachiku Joint Venture), main contractor',
      client: 'Directorate General of Sea Transportation',
      status: 'DONE',
      description: 'Construction of a new breakwater at Patimban Port, package 2.',
    },
    {
      id: 6,
      code: 'BENOA',
      title: 'Port of Benoa, jetty basin dredging',
      type: 'Dredging',
      period: 'Jan 2019 - Jan 2020',
      company: 'Van Oord, main contractor',
      client: 'PT Pelabuhan Indonesia III',
      status: 'DONE',
      description: 'Dredging of the jetty basin at Port of Benoa.',
    },
    {
      id: 7,
      code: 'TEMAS',
      title: 'Tanjung Emas, maintenance dredging',
      type: 'Dredging',
      period: 'Jan 2018 - Jan 2019',
      company: 'Boskalis, main contractor',
      client: 'PT Pelabuhan Indonesia III',
      status: 'DONE',
      description: 'Maintenance dredging at Pelabuhan Dalam.',
    },
    {
      id: 8,
      code: 'TJB56',
      title: 'PLTU Tanjung Jati B, units 5 and 6',
      type: 'Power',
      period: 'Jan 2018 - Dec 2018',
      company: 'Mitsui Engineering and Shipbuilding, main contractor',
      client: 'PT Bumi Jati Power',
      status: 'DONE',
      description: 'GRP pipe intake and outfall installation.',
    },
    {
      id: 9,
      code: 'BTNG1',
      title: 'PLTU Batang 2x1000MW, dredging and GRP pipe',
      type: 'Power',
      period: 'Sep 2017 - Jan 2018',
      company: 'Mitsui Engineering and Shipbuilding, main contractor',
      client: 'PT Bumisena Power Indonesia',
      status: 'DONE',
      description: 'Dredging of the jetty area, and GRP pipe intake and outfall installation.',
    },
  ];

  return (
    <section id="projects" className="section-rule bg-btc-surface">
      <div className="container-max section-padding py-24">
        <SectionHeader
          number="06"
          label="Projects"
          title="Works"
          kicker="Nine projects, from the PDSI rig through September 2026 back to PLTU Batang in 2017."
        />

        <div className="surface-card overflow-hidden">
          {projects.map((project, index) => {
            const open = expandedId === project.id;
            return (
              <article
                key={project.id}
                className={index !== projects.length - 1 ? 'border-b border-white/10' : ''}
              >
                <button
                  type="button"
                  onClick={() => setExpandedId(open ? null : project.id)}
                  className={`grid w-full min-h-11 grid-cols-12 items-center gap-2 p-5 text-left transition-colors duration-200 md:p-6 ${
                    open ? 'bg-white/5' : 'hover:bg-white/5'
                  }`}
                  aria-expanded={open}
                >
                  <div className="col-span-3 md:col-span-2">
                    <p className="label">{project.code}</p>
                    <p className="mt-1 hidden font-mono text-[10px] uppercase tracking-widest text-btc-muted md:block">
                      {project.type}
                    </p>
                  </div>
                  <div className="col-span-7 md:col-span-8">
                    <h3 className="font-heading font-semibold leading-tight text-white">
                      {project.title}
                    </h3>
                    <p className="mt-1 font-mono text-xs text-btc-muted">
                      {project.period}
                    </p>
                  </div>
                  <div className="col-span-2 flex items-center justify-end gap-3">
                    {project.status === 'ACTIVE' && (
                      <span className="hidden font-mono text-[10px] uppercase tracking-widest text-btc-gold sm:inline">
                        Active
                      </span>
                    )}
                    <Plus
                      size={20}
                      strokeWidth={2}
                      className={`text-btc-orange transition-transform duration-200 ${open ? 'rotate-90' : ''}`}
                    />
                  </div>
                </button>

                {open && (
                  <div className="border-t border-white/10 bg-btc-void p-6 md:p-8">
                    <div className="grid gap-6 sm:grid-cols-2">
                      <div>
                        <p className="label">Company</p>
                        <p className="mt-1 text-white">{project.company}</p>
                      </div>
                      {project.client && (
                        <div>
                          <p className="label">Client</p>
                          <p className="mt-1 text-white">{project.client}</p>
                        </div>
                      )}
                      <div className={project.client ? '' : 'sm:col-span-2'}>
                        <p className="label">Scope</p>
                        <p className="mt-1 text-btc-muted">{project.description}</p>
                      </div>
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </div>

        <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-4">
          {[
            { label: 'Total', value: String(projects.length) },
            { label: 'Latest', value: 'Sep 2026' },
            { label: 'From', value: 'Sep 2017' },
            { label: 'Sectors', value: 'Drilling, port, power' },
          ].map((item) => (
            <div key={item.label} className="surface-card p-6">
              <p className="label">{item.label}</p>
              <p className="mt-2 font-heading text-lg font-semibold text-white">{item.value}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;
